import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPortalUser } from '../services/portalAuth';
import { portalApi } from '../services/portalApi';
import './PortalHomePage.css';

function money(v) { return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}` }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—' }

function StatusBadge({ status }) {
  const map = {
    Draft:  ['badge-draft',   'Draft'],
    Sent:   ['badge-action',  'Awaiting signature'],
    Signed: ['badge-done',    'Signed'],
    Paid:   ['badge-done',    'Paid'],
    Unpaid: ['badge-action',  'Unpaid'],
  };
  const [cls, label] = map[status] || ['badge-draft', status];
  return <span className={`portal-badge ${cls}`}>{label}</span>;
}

export default function PortalHomePage() {
  const user = getPortalUser();
  const [contracts, setContracts] = useState(null);
  const [invoices,  setInvoices]  = useState(null);
  const [error,     setError]     = useState('');

  useEffect(() => {
    Promise.all([portalApi.contracts(), portalApi.invoices()])
      .then(([c, inv]) => { setContracts(c); setInvoices(inv); })
      .catch(() => setError('Unable to load your portal. Please refresh.'));
  }, []);

  const pendingContracts = contracts?.filter(c => c.status === 'Sent') ?? [];
  const signedContracts  = contracts?.filter(c => c.status === 'Signed') ?? [];
  const unpaidInvoices   = invoices?.filter(i => i.status === 'Sent') ?? [];
  const paidInvoices     = invoices?.filter(i => i.status === 'Paid') ?? [];

  const loading = contracts === null && !error;

  const greeting = user?.name ? `Welcome back, ${user.name.split(' ')[0]}.` : 'Welcome.';

  return (
    <div className="portal-home">
      <div className="ph-greeting">
        <h1 className="ph-title">{greeting}</h1>
        <p className="ph-sub">Here's a summary of your agreements and payments with Semper Gratus Inc.</p>
      </div>

      {error && <div className="ph-error">{error}</div>}

      {loading && (
        <div className="ph-loading">
          <div className="ph-skeleton" />
          <div className="ph-skeleton ph-skeleton-sm" />
          <div className="ph-skeleton" />
        </div>
      )}

      {!loading && !error && (
        <>
          {/* ── Action Required ───────────────────────────────────────── */}
          {(pendingContracts.length > 0 || unpaidInvoices.length > 0) && (
            <section className="ph-section">
              <div className="ph-section-label">
                <span className="ph-section-dot dot-action" />
                Action required
              </div>

              {pendingContracts.map((c, i) => (
                <Link
                  key={c.id}
                  to={`/portal/contracts/${c.id}`}
                  className="ph-card ph-card-action"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="ph-card-left">
                    <div className="ph-card-type">Contract</div>
                    <div className="ph-card-title">{c.title}</div>
                    <div className="ph-card-meta">Sent {fmtDate(c.createdAt)}</div>
                  </div>
                  <div className="ph-card-right">
                    <StatusBadge status="Sent" />
                    <span className="ph-card-arrow">→</span>
                  </div>
                </Link>
              ))}

              {unpaidInvoices.map((inv, i) => (
                <Link
                  key={inv.id}
                  to={`/portal/invoices/${inv.id}`}
                  className="ph-card ph-card-action"
                  style={{ animationDelay: `${(pendingContracts.length + i) * 60}ms` }}
                >
                  <div className="ph-card-left">
                    <div className="ph-card-type">Invoice</div>
                    <div className="ph-card-title">{inv.description}</div>
                    <div className="ph-card-meta">Due {fmtDate(inv.dueDate)} · {money(inv.amountDue)}</div>
                  </div>
                  <div className="ph-card-right">
                    <StatusBadge status="Unpaid" />
                    <span className="ph-card-arrow">→</span>
                  </div>
                </Link>
              ))}
            </section>
          )}

          {/* ── All clear ─────────────────────────────────────────────── */}
          {pendingContracts.length === 0 && unpaidInvoices.length === 0 && (
            <div className="ph-clear">
              <span className="ph-clear-icon" aria-hidden="true">✓</span>
              <p>You're all caught up — no pending signatures or payments.</p>
            </div>
          )}

          {/* ── History ───────────────────────────────────────────────── */}
          {(signedContracts.length > 0 || paidInvoices.length > 0) && (
            <section className="ph-section ph-section-history">
              <div className="ph-section-label">
                <span className="ph-section-dot dot-done" />
                History
              </div>

              {signedContracts.map((c, i) => (
                <Link
                  key={c.id}
                  to={`/portal/contracts/${c.id}`}
                  className="ph-card ph-card-history"
                  style={{ animationDelay: `${i * 40}ms` }}
                >
                  <div className="ph-card-left">
                    <div className="ph-card-type">Contract</div>
                    <div className="ph-card-title">{c.title}</div>
                    <div className="ph-card-meta">Signed {fmtDate(c.signedAt)}</div>
                  </div>
                  <div className="ph-card-right">
                    <StatusBadge status="Signed" />
                    {c.pdfUrl && <span className="ph-pdf-hint">PDF</span>}
                    <span className="ph-card-arrow">→</span>
                  </div>
                </Link>
              ))}

              {paidInvoices.map((inv, i) => (
                <Link
                  key={inv.id}
                  to={`/portal/invoices/${inv.id}`}
                  className="ph-card ph-card-history"
                  style={{ animationDelay: `${(signedContracts.length + i) * 40}ms` }}
                >
                  <div className="ph-card-left">
                    <div className="ph-card-type">Invoice</div>
                    <div className="ph-card-title">{inv.description}</div>
                    <div className="ph-card-meta">Paid {fmtDate(inv.paidAt)} · {money(inv.amountDue)}</div>
                  </div>
                  <div className="ph-card-right">
                    <StatusBadge status="Paid" />
                    <span className="ph-card-arrow">→</span>
                  </div>
                </Link>
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
}
